declare global {
  interface Window {
    env?: {
      STELLA_ENV: string;
    };
  }
}

export const getRoleLabel = (
  isAdmin: boolean,
  isManager: boolean,
  isCompanyAdmin: boolean
): string => {
  let role = '';
  if (isAdmin) {
    role = 'Agent Connect Admin';
  } else if (isCompanyAdmin) {
    role = 'Company Admin';
  } else if (isManager) {
    role = 'Manager';
  } else {
    role = 'Employee';
  }
  return role;
};

export const getStellaEnv = (): string | undefined => {
  return window && window.env && window.env.STELLA_ENV;
};

export const pluralOrSingular = (text: string, numResults: number): string => {
  if (!numResults || numResults === 0) {
    return `${text}s`;
  }
  return numResults > 1 ? `${text}s` : text;
};

export const createToastMessage = ({
  content,
  level,
}: {
  content: string;
  level: string;
}) => {
  const standardToastMessage = {
    content,
    level,
    key: Date.now(),
    isDismissible: true,
    shouldAutoDismiss: true,
  };
  const toastTypeObj = {
    confirm: () => {
      return {
        ...standardToastMessage,
        level: 'confirm',
      };
    },
    error: () => {
      return {
        ...standardToastMessage,
        level: 'error',
      };
    },
  };
  return toastTypeObj[level]();
};

export const getUniqueKey = () => {
  return (
    Date.now().toString(36) + Math.random().toString(36).substring(2, 5)
  ).toUpperCase();
};

export const capitalize = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const statusPoll = async (
  status: { (): Promise<string> },
  statusCondition: { (status: string): boolean },
  ms: number | undefined
) => {
  const wait = (ms = 1000) => new Promise(resolve => setTimeout(resolve, ms));
  let result = await status();
  while (!statusCondition(result)) {
    await wait(ms); // eslint-disable-line no-await-in-loop
    result = await status(); // eslint-disable-line no-await-in-loop
  }
  return result;
};

interface Operation {
  employee: string;
  to_add: string[];
  to_remove: string[];
}

type PreviewDetails = {
  warnings: string[];
  operations: Operation[];
};

interface ApolloErrorObj {
  graphQLErrors: {
    message: string;
  }[];
  networkError: {
    result: {
      errors: {
        message: string;
      }[];
    };
  };
}

export const formatPreviewDetails = (previewDetails: PreviewDetails) => {
  const { operations } = previewDetails;

  const newOperations = operations.filter(operation => {
    const { to_add: toAddArr, to_remove: toRemoveArr } = operation;
    return !(toAddArr.length === 0 && toRemoveArr.length === 0);
  });

  return {
    ...previewDetails,
    operations: newOperations,
  };
};

export const formatErrors = (errorObj: ApolloErrorObj) => {
  const { graphQLErrors = [], networkError } = errorObj || {};
  const { errors = [] } = networkError?.result || {};

  const serverErrors = graphQLErrors.map(({ message }) =>
    createToastMessage({
      level: 'error',
      content: message,
    })
  );

  const clientErrors = errors.map(({ message }) =>
    createToastMessage({
      level: 'error',
      content: message,
    })
  );
  return [...serverErrors, ...clientErrors];
};

export const indexEntitiesByKey = (entityList, key = 'id') => {
  return (
    entityList?.reduce((acc, curr) => {
      acc[curr[key]] = curr;
      return acc;
    }, {}) ?? {}
  );
};

export const decodeHtml = html => {
  const element = document.createElement('textarea');
  element.innerHTML = html;
  return element.value;
};

export type Verb = 'is' | 'has' | 'was' | 'does';

export const pluralizedVerbs = {
  is: 'are',
  has: 'have',
  was: 'were',
  does: 'do',
};

/**
 * Returns the pluralized form of a verb based on the count.
 * @param verb - The verb to pluralize.
 * @param count - The count to determine the plural form.
 * @returns The pluralized form of the verb.
 */
export const pluralizeVerb = (verb: Verb, count: number): string => {
  return count === 1 ? verb : pluralizedVerbs[verb];
};


/**
 * Removes multiple consecutive spaces from a string and replaces them with single spaces.
 *
 * @param {string} input - The input string.
 * @returns {string} - The string with multiple spaces replaced by single spaces.
 */
export const removeMultipleSpaces = input => {
  return input.replace(/\s+/g, ' ').trim();
};

/**
 * Formats a list of items into a string with a specified separator and conjunction.
 *
 * @param items - The list of items to format.
 * @param separator - The separator to use between items. Default is ','.
 * @param conjunction - The conjunction to use before the last item. Default is 'and'.
 * @returns The formatted string.
 */
export const listToFormattedStr = (unfilteredItems, separator = ',', conjunction = 'and') => {
  const items = unfilteredItems.map(item => item?.trim?.() ?? item).filter(Boolean);
  if (!items || !items.length) {
    return '';
  } else if (items.length === 1) {
    return items[0];
  } else if (items.length === 2) {
    return `${items[0]} ${conjunction} ${items[1]}`;
  } else {
    const lastItem = items.pop();
    return removeMultipleSpaces(`${items.join(`${separator} `)} ${conjunction} ${lastItem}`);
  }
};

export const toTitleCase = (string: string) => {
  if (!string) {
    return '';
  }
  return string
    .toLowerCase()
    .replaceAll('_', ' ')
    .replace(/\b\w/g, s => s.toUpperCase());
};

/**
 * Strips HTML tags from a string except for <a> tags.
 *
 * @param inputStr - The input string.
 * @returns The string with HTML tags stripped except for <a> tags.
 */

export const stripTags = (inputStr: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(inputStr, 'text/html');

  doc.body.querySelectorAll('*:not(a)').forEach(element => {
      element.replaceWith(...Array.from(element.childNodes));
  });

  return doc.body.innerHTML;
};
